import { useQuery } from '@apollo/client'
import { Box, CircularProgress, Stack, Tab, Tabs } from '@mui/material'
import { type FC } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { RecruiterMe } from 'recruiter/graphql/admin.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import { AdminHeader } from '../AdminHeader'
import { ConversationsTab } from './ConversationsTab'
import { LogsTab } from './LogsTab'
import { OverviewTab } from './OverviewTab'

type DashboardTab = 'overview' | 'conversations' | 'logs'

const TABS: Array<{ value: DashboardTab; label: string }> = [
    { value: 'overview', label: 'Overview' },
    { value: 'conversations', label: 'Conversations' },
    { value: 'logs', label: 'Logs' },
]

const BotAuditDashboard: FC = () => {
    const { data, loading } = useQuery(RecruiterMe)
    const [searchParams] = useSearchParams()

    const isAdmin = data?.recruiterMe?.isAdmin ?? false
    const isBotAdmin = data?.recruiterMe?.isBotAdmin ?? false
    const activeTab =
        (searchParams.get('tab') as DashboardTab | null) ?? 'overview'

    if (loading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        )
    }

    if (!isBotAdmin) {
        return <Navigate to="/" replace />
    }

    const buildPath = (tab: DashboardTab) => {
        const params = new URLSearchParams(searchParams)
        params.set('tab', tab)
        return `${recruiterRouteConfig.botAudit.path}?${params.toString()}`
    }

    return (
        <Stack
            spacing={2}
            sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}
            data-testid="bot-audit-page"
        >
            <AdminHeader isAdmin={isAdmin} isBotAdmin={isBotAdmin} />
            <Tabs
                value={activeTab}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
                {TABS.map(({ value, label }) => (
                    <Tab
                        key={value}
                        value={value}
                        label={label}
                        component={Link}
                        to={buildPath(value)}
                        sx={{ textTransform: 'none' }}
                        data-testid={`bot-audit-tab-${value}`}
                    />
                ))}
            </Tabs>
            <Box>
                {activeTab === 'overview' && <OverviewTab />}
                {activeTab === 'conversations' && <ConversationsTab />}
                {activeTab === 'logs' && <LogsTab />}
            </Box>
        </Stack>
    )
}

export default BotAuditDashboard
